import { Component, OnDestroy } from '@angular/core';
import { AuthService, EntityService, SharedService } from '@app/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PmDemoModeService } from '../../services/pm-demo-mode.service';
import { PmConnectFormCityOption, PmConnectLeadPayload, PriceDiscoveryService } from '../../services/price-discovery.service';

@Component({
    selector: 'app-connect-lead-modal',
    templateUrl: './connect-lead-modal.component.html',
    styleUrls: ['./connect-lead-modal.component.scss']
})
export class ConnectLeadModalComponent implements OnDestroy {
    isConnectLeadPopupOpen = false;
    isConnectLeadSubmitting = false;
    isConnectLeadCitiesLoading = false;
    connectLeadCityOptions: PmConnectFormCityOption[] = [];
    filteredConnectLeadCities: PmConnectFormCityOption[] = [];
    showConnectLeadCityDropdown = false;
    connectLeadCityQuery = '';
    connectLeadForm = {
        full_name: '',
        phone_number: '',
        email: '',
        city: '',
        business_name: ''
    };
    connectLeadErrors: Partial<Record<keyof PmConnectLeadPayload, string>> = {};

    private destroy$ = new Subject<void>();

    constructor(
        private authService: AuthService,
        private entityService: EntityService,
        private priceDiscoveryService: PriceDiscoveryService,
        private sharedService: SharedService,
        private pmDemoMode: PmDemoModeService
    ) { }

    get isPublicDemo(): boolean {
        return this.pmDemoMode.isActive;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        document.body.style.overflow = '';
    }

    open(): void {
        const user = this.authService.user;
        const entity = this.entityService.getSingleSelectedEntity();
        this.connectLeadErrors = {};
        this.connectLeadForm = {
            full_name: user?.name || '',
            phone_number: this.formatConnectLeadPhone(user?.mobile || ''),
            email: user?.email || '',
            city: '',
            business_name: entity?.name || user?.company?.name || ''
        };
        this.connectLeadCityQuery = '';
        this.showConnectLeadCityDropdown = false;
        this.isConnectLeadPopupOpen = true;
        document.body.style.overflow = 'hidden';
        this.loadConnectLeadCities();
    }

    closeConnectLeadPopup(): void {
        this.isConnectLeadPopupOpen = false;
        this.showConnectLeadCityDropdown = false;
        this.isConnectLeadSubmitting = false;
        document.body.style.overflow = '';
    }

    onConnectLeadCityQueryChange(query: string): void {
        this.connectLeadCityQuery = query;
        this.connectLeadForm.city = '';
        this.showConnectLeadCityDropdown = true;
        const normalized = (query || '').trim().toLowerCase();
        const matched = !normalized
            ? this.connectLeadCityOptions
            : this.connectLeadCityOptions.filter(option =>
                option.label.toLowerCase().includes(normalized) ||
                option.value.toLowerCase().includes(normalized)
            );
        this.filteredConnectLeadCities = matched.slice(0, 50);
    }

    selectConnectLeadCity(option: PmConnectFormCityOption): void {
        this.connectLeadForm.city = option.value;
        this.connectLeadCityQuery = option.label;
        this.showConnectLeadCityDropdown = false;
        this.connectLeadErrors.city = undefined;
    }

    submitConnectLeadForm(): void {
        if (this.isConnectLeadSubmitting) {
            return;
        }
        if (!this.validateConnectLeadForm()) {
            return;
        }

        const payload: PmConnectLeadPayload = {
            full_name: this.connectLeadForm.full_name.trim(),
            phone_number: this.normalizeConnectLeadPhone(this.connectLeadForm.phone_number),
            email: this.connectLeadForm.email.trim(),
            city: this.connectLeadForm.city.trim(),
            business_name: this.connectLeadForm.business_name.trim()
        };

        this.isConnectLeadSubmitting = true;
        this.priceDiscoveryService.submitPmConnectLead(payload)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isConnectLeadSubmitting = false;
                    this.closeConnectLeadPopup();
                    this.sharedService.successAlert('Thanks! Our team will help you connect with suppliers shortly.');
                },
                error: (error) => {
                    this.isConnectLeadSubmitting = false;
                    const message = error?.error?.message || error?.message || 'Failed to submit details. Please try again.';
                    this.sharedService.errorAlert(message);
                }
            });
    }

    private loadConnectLeadCities(): void {
        if (this.connectLeadCityOptions.length > 0) {
            this.filteredConnectLeadCities = this.connectLeadCityOptions.slice(0, 50);
            return;
        }

        this.isConnectLeadCitiesLoading = true;
        this.priceDiscoveryService.getPmConnectFormCities()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (cities) => {
                    this.connectLeadCityOptions = cities;
                    this.filteredConnectLeadCities = cities.slice(0, 50);
                    this.isConnectLeadCitiesLoading = false;
                },
                error: () => {
                    this.isConnectLeadCitiesLoading = false;
                    this.sharedService.errorAlert('Failed to load cities. Please try again.');
                }
            });
    }

    private formatConnectLeadPhone(raw: string): string {
        const digits = String(raw || '').replace(/\D/g, '');
        if (digits.length === 10) {
            return `+91 ${digits}`;
        }
        if (digits.length === 12 && digits.startsWith('91')) {
            return `+91 ${digits.slice(2)}`;
        }
        return raw || '';
    }

    private normalizeConnectLeadPhone(raw: string): string {
        const digits = String(raw || '').replace(/\D/g, '');
        if (digits.length === 10) {
            return `+91 ${digits}`;
        }
        if (digits.length === 12 && digits.startsWith('91')) {
            return `+91 ${digits.slice(2)}`;
        }
        return String(raw || '').trim();
    }

    private validateConnectLeadForm(): boolean {
        const errors: Partial<Record<keyof PmConnectLeadPayload, string>> = {};
        const fullName = this.connectLeadForm.full_name.trim();
        const email = this.connectLeadForm.email.trim();
        const city = this.connectLeadForm.city.trim();
        const businessName = this.connectLeadForm.business_name.trim();
        const phone = this.normalizeConnectLeadPhone(this.connectLeadForm.phone_number);
        const phoneDigits = phone.replace(/\D/g, '');

        if (!fullName) {
            errors.full_name = 'Full name is required';
        }
        if (!(phoneDigits.length === 12 && phoneDigits.startsWith('91'))) {
            errors.phone_number = 'Enter a valid 10-digit phone number';
        }
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            errors.email = 'Enter a valid email';
        }
        if (!city) {
            errors.city = 'Please select a city from the list';
        }
        if (!businessName) {
            errors.business_name = 'Business name is required';
        }

        this.connectLeadErrors = errors;
        return Object.keys(errors).length === 0;
    }
}
